import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export type ToggleState = 'open' | 'closed';

type AnimationState = ToggleState | 'opening' | 'closing';

export type UseAnimatedHeightProps = {
  duration?: number;
  easing?: string;
  initialState?: ToggleState;
};

export type UseAnimatedHeightReturn<ElementType> = {
  ref: React.RefObject<ElementType>;
  toggle: () => void;
  isOpen: boolean;
  initialOpenStyleForSSR?: React.CSSProperties;
  initialClosedStyleForSSR?: React.CSSProperties;
};

const initialOpenStyleForSSR: React.CSSProperties = {
  overflow: 'hidden',
  visibility: 'visible',
};

const initialClosedStyleForSSR: React.CSSProperties = {
  overflow: 'hidden',
  visibility: 'hidden',
  height: '0px',
};

const defaults: Required<Pick<UseAnimatedHeightProps, 'duration' | 'easing' | 'initialState'>> = {
  duration: 250,
  easing: 'ease',
  initialState: 'closed',
};

// React 18 currently throws a warning when using useLayoutEffect on the server.
// To get around it, we can conditionally use useEffect on the server and useLayoutEffect
// in the browser. This workaround is also used by react-redux and other libs.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export const useAnimatedHeight = <ElementType extends HTMLElement>(
  props?: UseAnimatedHeightProps,
): UseAnimatedHeightReturn<ElementType> => {
  const [state, setState] = useState<AnimationState>(
    props?.initialState !== undefined ? props.initialState : defaults.initialState,
  );
  const ref = useRef<ElementType>(null);
  const easing = props?.easing !== undefined ? props.easing : defaults.easing;
  const duration = props?.duration !== undefined ? props.duration : defaults.duration;

  const openTransitionEndHandler = useCallback((): void => {
    if (!ref.current) return;
    setState('open');
    ref.current.removeEventListener('transitionend', openTransitionEndHandler);
    ref.current.style.height = ''; // Set height to its initial value (auto) at the end of the transition
  }, []);

  const closeTransitionEndHandler = useCallback((): void => {
    if (!ref.current) return;
    setState('closed');
    ref.current.removeEventListener('transitionend', closeTransitionEndHandler);
    ref.current.style.visibility = 'hidden';
  }, []);

  const open = (): void => {
    if (!ref.current) return;
    setState('opening');
    ref.current.style.visibility = 'visible';
    ref.current.style.height = ref.current.scrollHeight + 'px';
    ref.current.addEventListener('transitionend', openTransitionEndHandler);
  };

  const close = (): void => {
    if (!ref.current) return;
    setState('closing');
    // On the next frame, explicitly set the element's height to its current pixel height,
    // so we aren't transitioning out of 'auto'
    requestAnimationFrame((): void => {
      if (!ref.current) return;
      ref.current.style.height = ref.current.scrollHeight + 'px';
      // On the next frame, have the element transition to height: 0,
      requestAnimationFrame((): void => {
        if (!ref.current) return;
        ref.current.style.height = '0px';
        ref.current.addEventListener('transitionend', closeTransitionEndHandler);
      });
    });
  };

  const toggle = (): void => {
    if (state === 'closed') return open();
    if (state === 'open') return close();
  };

  // Here `useLayoutEffect` is used instead of `useEffect`
  // because this has to run before the first render to avoid flickering
  useIsomorphicLayoutEffect((): void => {
    if (!ref.current) return;
    ref.current.style.overflow = 'hidden';
    ref.current.style.transition = `height ${duration}ms ${easing}`;

    switch (state) {
      case 'open':
        ref.current.style.visibility = 'visible';
        break;
      case 'closed':
        ref.current.style.visibility = 'hidden';
        ref.current.style.height = '0px';
    }
  }, []);

  const isOpen = state === 'open' || state === 'opening' || state === 'closing';
  return { ref, toggle, isOpen, initialOpenStyleForSSR, initialClosedStyleForSSR };
};
